import { useCallback, useState } from 'react';
import type { FieldSuggestion } from '../../types/pipelineConfig';
import { FieldPathInput } from './FieldPathInput';

interface FieldTagListProps {
  fields: string[];
  onChange: (fields: string[]) => void;
  suggestions: FieldSuggestion[];
  placeholder?: string;
  disabled?: boolean;
}

export function FieldTagList({
  fields,
  onChange,
  suggestions,
  placeholder = 'Add a field path…',
  disabled = false,
}: FieldTagListProps) {
  const [inputValue, setInputValue] = useState('');

  const addField = useCallback(
    (path: string) => {
      const trimmed = path.trim();
      if (!trimmed) return;
      // Prevent duplicates
      if (fields.includes(trimmed)) {
        setInputValue('');
        return;
      }
      onChange([...fields, trimmed]);
      setInputValue('');
    },
    [fields, onChange],
  );

  const removeField = useCallback(
    (index: number) => {
      onChange(fields.filter((_, i) => i !== index));
    },
    [fields, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addField(inputValue);
    }
    // Backspace on empty input removes last tag
    if (e.key === 'Backspace' && inputValue === '' && fields.length > 0) {
      removeField(fields.length - 1);
    }
  };

  // Filter out already-selected paths from suggestions
  const availableSuggestions = suggestions.filter(
    (s) => !fields.includes(s.path),
  );

  return (
    <div className="space-y-2">
      {/* Tag chips */}
      {fields.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {fields.map((field, index) => (
            <span
              key={`${field}-${index}`}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/40 px-3 py-1 text-sm font-mono text-blue-800 dark:text-blue-200"
            >
              {field}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeField(index)}
                  className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 hover:text-blue-900 dark:hover:text-blue-100"
                  aria-label={`Remove ${field}`}
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Input for adding new fields */}
      {!disabled && (
        <div onKeyDown={handleKeyDown}>
          <FieldPathInput
            value={inputValue}
            onChange={(val) => {
              // If the value matches a suggestion exactly, add it directly
              const exactMatch = availableSuggestions.find((s) => s.path === val);
              if (exactMatch) {
                addField(val);
              } else {
                setInputValue(val);
              }
            }}
            suggestions={availableSuggestions}
            placeholder={fields.length === 0 ? placeholder : 'Add another field…'}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Type a field path and press Enter, or select from suggestions.
          </p>
        </div>
      )}

      {/* Empty state */}
      {fields.length === 0 && disabled && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          No fields configured.
        </p>
      )}
    </div>
  );
}
